import { Permission } from '@procureai/permissions';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractDataGridColumnGroup, ContractDataGridField, ContractDataGridRow, I18nNamespace } from '~/common/types';
import {
  ActiveCell,
  cellFactory,
  ChipCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  GridSortDirectionType,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  ListCountCell,
  numberFormatterFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { commodityGroupValueGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { IconId } from '~/components/Icon';
import { ListCountCellMenu } from '~/components/ListCountCellMenu/ListCountCellMenu';
import { LocationLink } from '~/components/LocationLink/LocationLink';
import { SourceLinksButton } from '~/components/SourceLinksButton/SourceLinksButton';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';
import { locationLabel } from '~/util/string';

export const useContractDataGridConfig: UseDataGridConfig<ContractDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.contract.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.contract.columnBands' });
  const { t: tCountries } = useTranslation(I18nNamespace.countries, {});
  const tLocation = useMemo(() => locationLabel(tCountries), [tCountries]);

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: ContractDataGridField.CONTRACT_CREATION_DATE, sort: GridSortDirectionType.DESC }],
      columnVisibilityModel: {
        [ContractDataGridField.CONTRACT_VALID_FROM]: false,
        [ContractDataGridField.CONTRACT_VALID_TO]: false,
        [ContractDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT]: false,
        [ContractDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION]: false,
        [ContractDataGridField.SUPPLIER_EXTERNAL_ID]: false,
        [ContractDataGridField.SUPPLIER_EMPLOYEE_COUNT]: false,
        [ContractDataGridField.SUPPLIER_INDUSTRY]: false,
        [ContractDataGridField.SUPPLIER_LEGAL_TYPE]: false,
        [ContractDataGridField.SUPPLIER_ADDRESS]: false,
        [ContractDataGridField.REPRESENTATIVE_COUNT]: false,
        [ContractDataGridField.CONTRACT_PRODUCT_COUNT]: false,
        [ContractDataGridField.CONTRACT_PRODUCT_CATEGORY_COUNT]: false,
        [ContractDataGridField.CONTRACT_SERVICE_COUNT]: false,
      },
      columns: [
        createColumn({
          field: ContractDataGridField.EXTERNAL_ID,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.contractDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 200,
          hideable: false,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_ACTIVE,
          renderCell: cellFactory(ActiveCell),
          minWidth: 100,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_CREATION_DATE,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_VALID_FROM,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_VALID_TO,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 170,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_NAME,
          renderCell: cellFactory(LinkCell, { needsPermission: Permission.BaseSupplierList }),
          valueGetter: (value, row): LinkCellValue => ({
            to: RouteId.supplierDetail(row.supplierId),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 350,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_EXTERNAL_ID,
          minWidth: 120,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_EMPLOYEE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 120,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_INDUSTRY,
          valueGetter: (value: ContractDataGridRow['supplierIndustry']) => value?.name,
          minWidth: 150,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_LEGAL_TYPE,
          valueGetter: (value: ContractDataGridRow['supplierLegalType']) => value?.name,
          minWidth: 150,
        }),
        createColumn({
          field: ContractDataGridField.SUPPLIER_ADDRESS,
          renderCell: cellFactory(LocationLink),
          valueFormatter: (value) => tLocation(value),
          minWidth: 600,
          sortable: false,
        }),
        createColumn({
          field: ContractDataGridField.REPRESENTATIVE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.representativeList) && (() => <ListCountCellMenu list={row.representativeList} />),
          })),
          minWidth: 140,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_PRODUCT_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.contractProductList) && (() => <ListCountCellMenu list={row.contractProductList} />),
          })),
          minWidth: 180,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_PRODUCT_CATEGORY_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.contractProductCategoryList) &&
              (() => <ListCountCellMenu list={row.contractProductCategoryList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: ContractDataGridField.CONTRACT_SERVICE_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList:
              notEmpty(row.contractServiceList) && (() => <ListCountCellMenu list={row.contractServiceList} />),
          })),
          minWidth: 180,
        }),
        createColumn({
          field: ContractDataGridField.ORDER_LAST_DATE,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 140,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.ORDER_VOLUME_YEAR,
          renderCell: cellFactory(CurrencyCell),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.ORDER_COUNT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: ContractDataGridField.PRIMARY_COMMODITY_GROUP,
          renderCell: cellFactory(ChipCell, ({ value }) => ({ value: commodityGroupValueGetter(value) })),
          minWidth: 120,
          sortable: false,
        }),
        createColumn({
          field: ContractDataGridField.COMMODITY_GROUP_COUNT,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            renderList: notEmpty(row.commodityGroupList) && (() => <ListCountCellMenu list={row.commodityGroupList} />),
          })),
          minWidth: 100,
        }),
        createColumn({
          field: ContractDataGridField.LINKS,
          renderCell: cellFactory(SourceLinksButton, ({ value }) => ({ links: value })),
          minWidth: 120,
          sortable: false,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: ContractDataGridColumnGroup.CONTRACT,
          renderHeaderGroup: headerGroupWithIcon(IconId.contract),
          children: [
            //
            { field: ContractDataGridField.EXTERNAL_ID },
            { field: ContractDataGridField.CONTRACT_ACTIVE },
            { field: ContractDataGridField.CONTRACT_CREATION_DATE },
            { field: ContractDataGridField.CONTRACT_VALID_FROM },
            { field: ContractDataGridField.CONTRACT_VALID_TO },
            { field: ContractDataGridField.CONTRACT_RECEIVED_PROPOSALS_COUNT },
            { field: ContractDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION },
            { field: ContractDataGridField.PRIMARY_COMMODITY_GROUP },
            { field: ContractDataGridField.COMMODITY_GROUP_COUNT },
            { field: ContractDataGridField.LINKS },
          ],
        }),
        createGroup({
          groupId: ContractDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: ContractDataGridField.SUPPLIER_NAME },
            { field: ContractDataGridField.SUPPLIER_EXTERNAL_ID },
            { field: ContractDataGridField.SUPPLIER_EMPLOYEE_COUNT },
            { field: ContractDataGridField.SUPPLIER_INDUSTRY },
            { field: ContractDataGridField.SUPPLIER_LEGAL_TYPE },
            { field: ContractDataGridField.SUPPLIER_ADDRESS },
            { field: ContractDataGridField.REPRESENTATIVE_COUNT },
          ],
        }),
        createGroup({
          groupId: ContractDataGridColumnGroup.PRODUCT,
          renderHeaderGroup: headerGroupWithIcon(IconId.Widgets),
          children: [
            //
            { field: ContractDataGridField.CONTRACT_PRODUCT_COUNT },
            { field: ContractDataGridField.CONTRACT_PRODUCT_CATEGORY_COUNT },
          ],
        }),
        createGroup({
          groupId: ContractDataGridColumnGroup.SERVICE,
          renderHeaderGroup: headerGroupWithIcon(IconId.Person),
          children: [
            //
            { field: ContractDataGridField.CONTRACT_SERVICE_COUNT },
          ],
        }),
        createGroup({
          groupId: ContractDataGridColumnGroup.ORDER,
          renderHeaderGroup: headerGroupWithIcon(IconId.order),
          children: [
            //
            { field: ContractDataGridField.ORDER_LAST_DATE },
            { field: ContractDataGridField.ORDER_VOLUME_YEAR },
            { field: ContractDataGridField.ORDER_COUNT_YEAR },
          ],
        }),
      ],
      exportModel: {
        [ContractDataGridField.CONTRACT_AVERAGE_VALUE_CONTRIBUTION]: {
          numFmt: '0.00%',
        },
        [ContractDataGridField.LINKS]: {
          disable: true,
        },
      },
    } as DataGridConfig<ContractDataGridRow>;
  }, [t, tLocation, tColumn, tGroup]);
};
